import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AGENCIES } from '../../graphql/queries/agency';
import {
  CREATE_AGENCY_BY_SUPERADMIN,
  UPDATE_AGENCY_BY_SUPERADMIN,
  DELETE_AGENCY,
} from '../../graphql/mutations/superadmin';
import { isSuperAdmin } from '../../lib/auth';
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  X,
  Loader2,
  ShieldAlert,
} from 'lucide-react';
import Modal from '../../components/Modal';

interface Agency {
  id: number;
  name: string;
  subdomain: string;
  domainType?: string;
  mainUserRole?: string | null;
  logo?: string | null;
  description?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  firstTitle?: string | null;
  secondTitle?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  tiktok?: string | null;
  yearsExperience?: number | null;
  expertise?: string | null;
  satisfiedTravelers?: number | null;
  theme?: Record<string, string> | null;
  popularDestinations?: Array<{ id: number; name: string; image: string; price: number }>;
  prestations?: string[] | null;
}

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data, loading, refetch } = useQuery<{ agencies: Agency[] }>(GET_AGENCIES, {
    fetchPolicy: 'network-only',
  });
  const [createAgency, { loading: creating }] = useMutation(CREATE_AGENCY_BY_SUPERADMIN, {
    onCompleted: () => {
      setSuccess('Agence créée avec succès.');
      setShowAddModal(false);
      resetAddForm();
      refetch();
    },
    onError: (e) => setError(e.message),
  });
  const [updateAgency, { loading: updating }] = useMutation(UPDATE_AGENCY_BY_SUPERADMIN, {
    onCompleted: () => {
      setSuccess('Agence mise à jour.');
      setEditingAgency(null);
      refetch();
    },
    onError: (e) => setError(e.message),
  });
  const [deleteAgencyMutation, { loading: deleting }] = useMutation(DELETE_AGENCY, {
    onCompleted: () => {
      setSuccess('Agence supprimée.');
      setDeletingId(null);
      refetch();
    },
    onError: (e) => setError(e.message),
  });

  // Add form state (same as register)
  const [addEmail, setAddEmail] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addRole, setAddRole] = useState('ADMIN');
  const [addAgencyName, setAddAgencyName] = useState('');
  const [addDomainType, setAddDomainType] = useState('SUBDOMAIN');
  const [addCustomDomain, setAddCustomDomain] = useState('');

  const resetAddForm = () => {
    setAddEmail('');
    setAddPassword('');
    setAddRole('ADMIN');
    setAddAgencyName('');
    setAddDomainType('SUBDOMAIN');
    setAddCustomDomain('');
    setError('');
  };

  const addSubdomain =
    addDomainType === 'SUBDOMAIN'
      ? addAgencyName.toLowerCase().replace(/[^a-z0-9]/g, '')
      : addCustomDomain.toLowerCase().replace(/[^a-z0-9\-.]/g, '');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!addEmail || !addPassword || !addAgencyName) {
      setError('Email, mot de passe et nom d\'agence requis.');
      return;
    }
    if (addDomainType === 'CUSTOM' && !addCustomDomain.trim()) {
      setError('Indiquez le domaine personnalisé.');
      return;
    }
    await createAgency({
      variables: {
        email: addEmail,
        password: addPassword,
        role: addRole,
        agencyName: addAgencyName,
        subdomain: addSubdomain,
        domainType: addDomainType,
      },
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAgency) return;
    setError('');
    await updateAgency({
      variables: {
        agencyId: editingAgency.id,
        input: {
          name: editingAgency.name,
          subdomain: editingAgency.subdomain,
          domainType: editingAgency.domainType || 'SUBDOMAIN',
          email: editingAgency.email ?? undefined,
        },
        mainUserRole: editingAgency.mainUserRole ?? undefined,
      },
    });
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Supprimer cette agence et toutes ses données ?')) {
      setDeletingId(id);
      deleteAgencyMutation({ variables: { id } });
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <ShieldAlert className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Accès réservé</h1>
          <p className="text-gray-600 mb-6">
            Cette page est réservée au créateur de la plateforme (super administrateur).
          </p>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  const agencies = data?.agencies ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Gestion des agences</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              Retour
            </button>
            <button
              type="button"
              onClick={() => { setError(''); setSuccess(''); setShowAddModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus className="w-5 h-5" />
              Ajouter une agence
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agence</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sous-domaine / Domaine</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {agencies.map((agency) => (
                  <tr key={agency.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-gray-400" />
                        <span className="font-medium text-gray-900">{agency.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {agency.domainType === 'CUSTOM'
                        ? agency.subdomain
                        : `${agency.subdomain}.ziyara.site`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{agency.email ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => { setError(''); setEditingAgency({ ...agency }); }}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(agency.id)}
                        disabled={deleting && deletingId === agency.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        title="Supprimer"
                      >
                        {deleting && deletingId === agency.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {agencies.length === 0 && (
              <div className="py-12 text-center text-gray-500">
                Aucune agence. Cliquez sur « Ajouter une agence » pour en créer une.
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modal Ajouter une agence (champs type register) */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetAddForm(); }}
        title="Ajouter une agence"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email du compte <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe <span className="text-red-500">*</span></label>
            <input
              type="password"
              value={addPassword}
              onChange={(e) => setAddPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
            <select
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="AGENT">Agent</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'agence <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={addAgencyName}
              onChange={(e) => setAddAgencyName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de domaine</label>
            <select
              value={addDomainType}
              onChange={(e) => setAddDomainType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="SUBDOMAIN">Sous-domaine Ziyara</option>
              <option value="CUSTOM">Domaine personnalisé</option>
            </select>
          </div>
          {addDomainType === 'CUSTOM' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL complète du domaine <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={addCustomDomain}
                onChange={(e) => setAddCustomDomain(e.target.value)}
                placeholder="ex: abc.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="mt-1 text-xs text-gray-500">Saisir l’URL entière du domaine (ex: abc.com)</p>
            </div>
          )}
          {addDomainType === 'SUBDOMAIN' && addAgencyName && (
            <p className="text-sm text-gray-500">
              Site : <strong>{addSubdomain || '(calculé)'}.ziyara.site</strong>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Créer l'agence
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Modifier une agence */}
      <Modal
        isOpen={!!editingAgency}
        onClose={() => setEditingAgency(null)}
        title="Modifier l'agence"
      >
        {editingAgency && (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l&apos;agence</label>
              <input
                type="text"
                value={editingAgency.name}
                onChange={(e) => setEditingAgency({ ...editingAgency, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={editingAgency.email ?? ''}
                onChange={(e) => setEditingAgency({ ...editingAgency, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de domaine</label>
              <select
                value={editingAgency.domainType || 'SUBDOMAIN'}
                onChange={(e) => setEditingAgency({ ...editingAgency, domainType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="SUBDOMAIN">Sous-domaine Ziyara</option>
                <option value="CUSTOM">Domaine personnalisé</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editingAgency.domainType === 'CUSTOM' ? 'URL complète du domaine (ex: abc.com)' : 'Sous-domaine (ex: monagence → monagence.ziyara.site)'}
              </label>
              <input
                type="text"
                value={editingAgency.subdomain}
                onChange={(e) => setEditingAgency({ ...editingAgency, subdomain: e.target.value })}
                placeholder={editingAgency.domainType === 'CUSTOM' ? 'abc.com' : 'monagence'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rôle (utilisateur principal)</label>
              <select
                value={editingAgency.mainUserRole ?? 'AGENT'}
                onChange={(e) => setEditingAgency({ ...editingAgency, mainUserRole: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="AGENT">Agent</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => setEditingAgency(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={updating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Enregistrer
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
